import { DecorItemI } from '../shared';

interface RoomSizes {
    length: number; // H8@8=0 :><=0BK 2 <<
    depth: number;  // 3;C18=0 :><=0BK 2 <<
    height: number; // 2KA>B0 :><=0BK 2 <<
}

interface Point2D {
    x: number;
    y: number;
}

interface WallBounds {
    front: number;  // Z = 0
    back: number;   // Z = depth/1000
    left: number;   // X = -length/2000
    right: number;  // X = length/2000
}

interface Ray {
    origin: Point2D;
    direction: Point2D;
    endName: string;
}

interface WallIntersection {
    wallName: 'front' | 'back' | 'left' | 'right';
    distance: number;
    point: Point2D;
}

interface DebugVisualization {
    rayA: Ray;
    rayB: Ray;
    intersectionsA: WallIntersection[];
    intersectionsB: WallIntersection[];
    targetWalls: [string, string];
    L1: number;
    L2: number;
}

/**
 * >;CG05B 3@0=8FK AB5= :><=0BK 2 2D :>>@48=0B0E (284 A25@EC)
 */
function getRoomWallBounds(roomSizes: RoomSizes): WallBounds {
    return {
        front: 0,                           // Z = 0
        back: roomSizes.depth / 1000,       // Z = depth/1000
        left: -roomSizes.length / 2000,     // X = -length/2000
        right: roomSizes.length / 2000,     // X = +length/2000
    };
}

/**
 * KG8A;O5B ?5@5A5G5=85 ;CG0 A ;8=859 AB5=K
 */
function rayWallIntersection(
    ray: Ray,
    wallName: 'front' | 'back' | 'left' | 'right',
    bounds: WallBounds
): WallIntersection | null {
    const { origin, direction } = ray;

    let intersectionPoint: Point2D | null = null;
    let t = Infinity;

    // KG8A;O5< ?5@5A5G5=85 A :064>9 AB5=>9
    switch (wallName) {
        case 'front': // Z = 0, 3>@87>=B0;L=0O ;8=8O
            if (Math.abs(direction.y) > 1e-10) {
                t = -origin.y / direction.y;
                intersectionPoint = { x: origin.x + t * direction.x, y: 0 };
            }
            break;
        case 'back': // Z = bounds.back, 3>@87>=B0;L=0O ;8=8O
            if (Math.abs(direction.y) > 1e-10) {
                t = (bounds.back - origin.y) / direction.y;
                intersectionPoint = { x: origin.x + t * direction.x, y: bounds.back };
            }
            break;
        case 'left': // X = bounds.left, 25@B8:0;L=0O ;8=8O
            if (Math.abs(direction.x) > 1e-10) {
                t = (bounds.left - origin.x) / direction.x;
                intersectionPoint = { x: bounds.left, y: origin.y + t * direction.y };
            }
            break;
        case 'right': // X = bounds.right, 25@B8:0;L=0O ;8=8O
            if (Math.abs(direction.x) > 1e-10) {
                t = (bounds.right - origin.x) / direction.x;
                intersectionPoint = { x: bounds.right, y: origin.y + t * direction.y };
            }
            break;
    }

    // @>25@O5< GB> ?5@5A5G5=85 =0E>48BAO 2 ?@545;0E AB5=K 8 2 ?>;>68B5;L=>< =0?@02;5=88 ;CG0
    if (intersectionPoint && t > 1e-10) {
        let isWithinWall = false;

        switch (wallName) {
            case 'front':
            case 'back':
                isWithinWall = intersectionPoint.x >= bounds.left && intersectionPoint.x <= bounds.right;
                break;
            case 'left':
            case 'right':
                isWithinWall = intersectionPoint.y >= bounds.front && intersectionPoint.y <= bounds.back;
                break;
        }

        if (isWithinWall) {
            return {
                wallName,
                distance: t,
                point: intersectionPoint
            };
        }
    }

    return null;
}

/**
 * 0E>48B 1;8609H55 ?5@5A5G5=85 ;CG0 A> AB5=0<8
 */
function findNearestWallIntersection(ray: Ray, bounds: WallBounds): WallIntersection | null {
    const walls: Array<'front' | 'back' | 'left' | 'right'> = ['front', 'back', 'left', 'right'];
    const intersections: WallIntersection[] = [];

    for (const wall of walls) {
        const intersection = rayWallIntersection(ray, wall, bounds);
        if (intersection) {
            intersections.push(intersection);
        }
    }

    // >72@0I05< 1;8609H55 ?5@5A5G5=85
    if (intersections.length > 0) {
        return intersections.reduce((closest, current) =>
            current.distance < closest.distance ? current : closest
        );
    }

    return null;
}

/**
 * A=>2=0O DC=:F8O @0AGQB0 @0ABO65=8O AB5=K ?> 0;3>@8B<C 87 indo.md
 */
export function calculateStretchToWallsAdvanced(
    decor: DecorItemI,
    roomSizes: RoomSizes
): {
    newWidth: number;
    newPosition: [number, number, number];
    debugVisualization?: DebugVisualization;
} {
    console.log("=� 0G8=05< @0AGQB @0ABO65=8O AB5=K ?> 0;3>@8B<C 87 indo.md");

    const { position, width, rotation } = decor;
    const [cx, cy, cz] = position; // F5=B@ AB5=K 2 <5B@0E

    // >;CG05< C3>; ?>2>@>B0 AB5=K (2 @0480=0E)
    const theta = -((rotation && typeof rotation === 'object') ? rotation.y : (rotation || 0));
    const cos = Math.cos(theta);
    const sin = Math.sin(theta);

    console.log(`=� &5=B@ AB5=K: (${cx.toFixed(3)}, ${cz.toFixed(3)}) 2 <5B@0E`);
    console.log(`=� #3>; ?>2>@>B0: ${(theta * 180 / Math.PI).toFixed(1)}�`);
    console.log(`=� "5:CI0O H8@8=0: ${(width * 1000).toFixed(0)} <<`);

    // @0=8FK AB5= 2 2D
    const bounds = getRoomWallBounds(roomSizes);
    console.log(`<� @0=8FK AB5=:`, bounds);

    // >>@48=0BK :>=F>2 AB5=K 2 2D (?@>5:F8O =0 ?;>A:>ABL XZ)
    const halfWidth = width / 2;
    const endA: Point2D = { x: cx - halfWidth * cos, y: cz - halfWidth * sin };
    const endB: Point2D = { x: cx + halfWidth * cos, y: cz + halfWidth * sin };

    console.log(`= >=5F A: (${endA.x.toFixed(3)}, ${endA.y.toFixed(3)})`);
    console.log(`= >=5F B: (${endB.x.toFixed(3)}, ${endB.y.toFixed(3)})`);

    // 0?@02;5=8O ;CG59 (24>;L >A8 AB5=K 2 >15 AB>@>=K)
    const directionA: Point2D = { x: -cos, y: -sin }; // >B F5=B@0 : :>=FC A 8 40;LH5
    const directionB: Point2D = { x: cos, y: sin };   // >B F5=B@0 : :>=FC B 8 40;LH5

    // !>740Q< ;CG8 87 :>=F>2 AB5=K
    const rayA: Ray = { origin: endA, direction: directionA, endName: 'A' };
    const rayB: Ray = { origin: endB, direction: directionB, endName: 'B' };

    console.log(`=� CG A: >B (${endA.x.toFixed(3)}, ${endA.y.toFixed(3)}) 2 =0?@02;5=88 (${directionA.x.toFixed(3)}, ${directionA.y.toFixed(3)})`);
    console.log(`=� CG B: >B (${endB.x.toFixed(3)}, ${endB.y.toFixed(3)}) 2 =0?@02;5=88 (${directionB.x.toFixed(3)}, ${directionB.y.toFixed(3)})`);

    // 0E>48< 1;8609H85 ?5@5A5G5=8O 4;O :064>3> ;CG0
    const intersectionA = findNearestWallIntersection(rayA, bounds);
    const intersectionB = findNearestWallIntersection(rayB, bounds);

    if (!intersectionA || !intersectionB) {
        console.error("L 5 C40;>AL =09B8 ?5@5A5G5=8O A> AB5=0<8");
        return {
            newWidth: width * 1000, // 2>72@0I05< 8AE>4=CN H8@8=C 2 <<
            newPosition: position
        };
    }

    console.log(`<� &5;520O AB5=0 A: ${intersectionA.wallName}, @0AAB>O=85: ${intersectionA.distance.toFixed(3)}`);
    console.log(`<� &5;520O AB5=0 B: ${intersectionB.wallName}, @0AAB>O=85: ${intersectionB.distance.toFixed(3)}`);

    // ?@545;O5< B8? 7040G8
    const wallPairs = [
        [intersectionA.wallName, intersectionB.wallName],
        [intersectionB.wallName, intersectionA.wallName]
    ];
    const isOpposite = wallPairs.some(([a, b]) =>
        (a === 'front' && b === 'back') ||
        (a === 'back' && b === 'front') ||
        (a === 'left' && b === 'right') ||
        (a === 'right' && b === 'left')
    );

    console.log(`= "8? 7040G8: ${isOpposite ? '@>B82>?>;>6=K5 AB5=K' : '!<56=K5 AB5=K'}`);

    // KG8A;O5< @0AAB>O=8O L1 8 L2
    const L1 = intersectionA.distance;
    const L2 = intersectionB.distance;

    console.log(`=� L1 (@0AAB>O=85 A): ${L1.toFixed(3)} <`);
    console.log(`=� L2 (@0AAB>O=85 B): ${L2.toFixed(3)} <`);

    // KG8A;O5< =>2CN 4;8=C (D>@<C;0 87 indo.md)
    const L_initial = width;
    const L_new = L_initial + L1 + L2;

    console.log(`=� AE>4=0O 4;8=0: ${L_initial.toFixed(3)} <`);
    console.log(`=� >20O 4;8=0: ${L_new.toFixed(3)} <`);

    // KG8A;O5< =>2K9 F5=B@ (D>@<C;0 87 indo.md)
    const centerShift = (L1 - L2) / 2;
    const cx_new = cx - centerShift * cos;
    const cz_new = cz - centerShift * sin;

    console.log(`=� !<5I5=85 F5=B@0: ${centerShift.toFixed(3)} <`);
    console.log(`=� >2K9 F5=B@: (${cx_new.toFixed(3)}, ${cz_new.toFixed(3)})`);

    // >43>B02;8205< >B;04>G=CN 287C0;870F8N
    const debugVisualization: DebugVisualization = {
        rayA,
        rayB,
        intersectionsA: [intersectionA],
        intersectionsB: [intersectionB],
        targetWalls: [intersectionA.wallName, intersectionB.wallName],
        L1,
        L2
    };

    console.log("  0AGQB 7025@HQ= CA?5H=>");

    return {
        newWidth: Math.round(L_new * 1000), // 2>72@0I05< 2 <<
        newPosition: [cx_new, cy, cz_new] as [number, number, number],
        debugVisualization
    };
}

/**
 * $C=:F8O 4;O @0ABO65=8O 2A5E 2=CB@5==8E AB5= (A>2<5AB8<>ABL A ACI5AB2CNI8< :>4><)
 */
export function stretchAllInnerWalls(
    decors: DecorItemI[],
    roomSizes: { width: number; height: number; depth: number },
    updateDecor: (decor: DecorItemI) => void
): void {
    const innerWalls = decors.filter(decor =>
        decor.type === 'constructions' && decor.code === 'vnutrennyaya-stena'
    );

    console.log(`='  0ABO38205< ${innerWalls.length} 2=CB@5==8E AB5=`);

    innerWalls.forEach((wall, index) => {
        console.log(`=( 1@010BK205< AB5=C ${index + 1}/${innerWalls.length}`);

        const { newWidth, newPosition } = calculateStretchToWallsAdvanced(wall, {
            length: roomSizes.width,
            depth: roomSizes.depth,
            height: roomSizes.height
        });

        updateDecor({
            ...wall,
            width: newWidth / 1000, // :>=25@B8@C5< >1@0B=> 2 <5B@K
            position: newPosition
        });
    });

    console.log(" A5 2=CB@5==85 AB5=K @0ABO=CBK");
}

/**
 * !>2<5AB8<>ABL A ACI5AB2CNI8< 8<5=5< DC=:F88
 */
export const stretchInnerWallToOuterWalls = calculateStretchToWallsAdvanced;